import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { api } from "../../lib/api";
import { useAction } from "../../lib/queries";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/Dialog";
import { useToast } from "../../ui/Feedback";
import { Field, FormGrid, Input, Select } from "../../ui/Form";

const slugify = (text: string) =>
  text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);

/**
 * Step 1 of onboarding (Buku Panduan 12.2): the project exists, its site key
 * is issued, and the specialist continues in Pengetahuan → Uji terima → Aktifkan.
 */
/** Catalog v4 website packages. */
export const PACKAGES = [
  ["PE", "Web Profil Esensial"],
  ["PM", "Web Profil Motion"],
  ["PS", "Web Profil Signature"],
  ["DE", "Web Dinamis Esensial"],
  ["DM", "Web Dinamis Motion"],
  ["DS", "Web Dinamis Signature"],
] as const;

export function NewProjectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [product, setProduct] = useState("ai-a");
  const addon = product === "ai-a" || product === "ai-al";
  const create = useAction((body: Record<string, unknown>) => api.post<{ id: string }>("/projects", body), [["overview"]]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const result = await create.mutateAsync({
      name,
      slug,
      clientName: form.get("clientName"),
      product,
      packageCode: addon ? form.get("packageCode") || null : null,
      websiteUrl: form.get("websiteUrl") || null,
      whatsapp: form.get("whatsapp") || null,
    });
    toast(`Proyek ${name} dibuat. Lanjutkan dengan mengisi basis pengetahuan.`);
    onClose();
    navigate(`/projects/${result.id}/knowledge`);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Proyek chatbot baru"
      description="Kunci situs untuk widget dibuat otomatis dan dikunci ke domain website."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button variant="primary" type="submit" form="new-project" loading={create.isPending}>
            Buat proyek
          </Button>
        </>
      }
    >
      <form id="new-project" onSubmit={submit}>
        <FormGrid>
          <Field label="Nama usaha (tampil di chatbot)">
            {(id) => (
              <Input
                id={id}
                required
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  if (!slugTouched) setSlug(slugify(event.target.value));
                }}
              />
            )}
          </Field>
          <Field label="Slug" hint="Huruf kecil dan tanda hubung, unik.">
            {(id, describedBy) => (
              <Input
                id={id}
                required
                value={slug}
                aria-describedby={describedBy}
                onChange={(event) => {
                  setSlugTouched(true);
                  setSlug(slugify(event.target.value));
                }}
              />
            )}
          </Field>
          <Field label="Nama klien di SPK">{(id) => <Input id={id} name="clientName" required placeholder="PT / CV / perorangan" />}</Field>
          <Field label="Produk">
            {(id) => (
              <Select id={id} value={product} onChange={(event) => setProduct(event.target.value)}>
                <optgroup label="Website buatan Navibiz">
                  <option value="ai-a">AI-A Asisten AI Add-on</option>
                  <option value="ai-al">AI-AL Add-on + Linkage</option>
                </optgroup>
                <optgroup label="Website luar, dipasang klien dengan panduan">
                  <option value="ai-p">AI-P Chatbot Dipandu</option>
                  <option value="ai-pl">AI-PL Dipandu + Linkage</option>
                </optgroup>
                <optgroup label="Website luar, dipasang tim Navibiz">
                  <option value="ai-k">AI-K Chatbot Dikerjakan</option>
                  <option value="ai-kl">AI-KL Dikerjakan + Linkage</option>
                </optgroup>
              </Select>
            )}
          </Field>
          {addon && (
            <Field label="Paket website">
              {(id) => (
                <Select id={id} name="packageCode" defaultValue="PM">
                  {PACKAGES.map(([code, label]) => (
                    <option key={code} value={code}>
                      {code} · {label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <Field label="Alamat website" hint="Dipakai untuk mengunci kunci situs ke domain ini.">
            {(id, describedBy) => <Input id={id} name="websiteUrl" type="url" placeholder="https://namausaha.biz.id" aria-describedby={describedBy} />}
          </Field>
          <Field label="WhatsApp usaha" hint="Tujuan tombol 'lanjut via WhatsApp'.">
            {(id, describedBy) => <Input id={id} name="whatsapp" inputMode="tel" placeholder="0812…" aria-describedby={describedBy} />}
          </Field>
        </FormGrid>
        {create.isError && <p style={{ color: "var(--critical-ink)", marginTop: 12 }}>{create.error.message}</p>}
      </form>
    </Dialog>
  );
}
